import { useTranslation } from '@hbs-us/ui';
import { getRouteApi } from '@tanstack/react-router';
import { RouteSkeleton } from '../_ops/RouteSkeleton.js';
import { MonthlyBillsView } from './MonthlyBillsView.js';
import { useMonthlyBillsQuery } from './useMonthlyBillsQuery.js';

/**
 * The `/monthly-bills` route component (a NEW route this plan). Reads the typed `month` search param,
 * fires the READ-ONLY coarse-endpoint query, and renders the faithful `MonthlyBillsView` on success.
 * Loading → a page-shaped `RouteSkeleton`; error → announced error state with a retry that refetches (the
 * estimates/invoices 3-state contract, verbatim tokens).
 *
 * The server computes the enriched bills + next-due + paid badges + the scheduled-this-month sum;
 * this component never re-aggregates and never sends a tenant id (SEC-11).
 */

const routeApi = getRouteApi('/monthly-bills');

export function MonthlyBillsRoute() {
  const { t } = useTranslation();
  const search = routeApi.useSearch();
  const query = useMonthlyBillsQuery(search);

  if (query.isPending) {
    return <RouteSkeleton label={t('monthlyBills:title')} rows={6} columns={4} filters={3} />;
  }

  if (query.isError) {
    return (
      <section
        role="alert"
        aria-live="assertive"
        className="flex h-full flex-col items-center justify-center gap-4 bg-background p-16 text-center"
      >
        <h1 className="font-display font-semibold text-2xl text-foreground">
          {t('monthlyBills:error.heading')}
        </h1>
        <p className="max-w-sm text-muted-foreground text-sm">{t('monthlyBills:error.body')}</p>
        <button
          type="button"
          onClick={() => void query.refetch()}
          className="inline-flex h-11 items-center justify-center rounded-md bg-secondary px-4 font-medium text-secondary-foreground text-sm ring-offset-background transition-colors hover:bg-secondary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {t('monthlyBills:error.retry')}
        </button>
      </section>
    );
  }

  return <MonthlyBillsView data={query.data} search={search} />;
}
